import React, { useState, useEffect } from 'react';
import { SwipeableCard } from '../components/common/SwipeableCard';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, startOfMonth, differenceInWeeks } from 'date-fns';
import { ko } from 'date-fns/locale';
import toast from 'react-hot-toast';
import api from '../utils/api';

interface ScheduleItem {
  id: string;
  userId: string;
  date: Date;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: Date;
  updatedAt: Date;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

type TabType = 'PENDING' | 'APPROVED' | 'REJECTED';

/**
 * 관리자 휴무 승인 페이지 (탭 구조 + 주간 페이지네이션)
 */
const AdminSchedulePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('PENDING');
  const [schedules, setSchedules] = useState<Record<TabType, ScheduleItem[]>>({
    PENDING: [],
    APPROVED: [],
    REJECTED: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // 주간 페이지네이션 (승인/거절 탭만)
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    startOfWeek(new Date(), { weekStartsOn: 0 }) // 일요일 시작
  );

  // 전체 데이터 로드
  const loadAllSchedules = async () => {
    setIsLoading(true);
    try {
      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        api.get('/schedule/pending'),
        api.get('/schedule/approved'),
        api.get('/schedule/rejected'),
      ]);

      setSchedules({
        PENDING: pendingRes.data.data.schedules || [],
        APPROVED: approvedRes.data.data.schedules || [],
        REJECTED: rejectedRes.data.data.schedules || [],
      });
    } catch (error: any) {
      console.error('휴무 목록 로드 실패:', error);
      toast.error(`목록을 불러올 수 없습니다. ${error.response?.data?.message || ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllSchedules();
  }, []);

  // 주간 필터링 (승인/거절만)
  const getFilteredSchedules = (items: ScheduleItem[]) => {
    if (activeTab === 'PENDING') return items; // 대기 중은 전체
    
    const weekStart = currentWeekStart;
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
    
    return items.filter((item) => {
      const processedDate = new Date(item.updatedAt); // 처리 날짜 기준
      return processedDate >= weekStart && processedDate <= weekEnd;
    });
  };

  const currentSchedules = getFilteredSchedules(schedules[activeTab]);

  // 주 이동
  const goToPrevWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  const goToNextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const goToThisWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const handleApprove = async (scheduleId: string) => {
    try {
      const response = await api.patch(`/schedule/${scheduleId}`, { status: 'APPROVED' });
      
      if (response.data.success) {
        const schedule = schedules.PENDING.find(s => s.id === scheduleId);
        toast.success(`${schedule?.user.name} 님의 휴무 신청을 승인했습니다! ✅`);
        loadAllSchedules(); // 전체 재로드
      }
    } catch (error) {
      console.error('승인 실패:', error);
      toast.error('승인에 실패했습니다.');
    }
  };

  // 거절
  const handleReject = async (scheduleId: string) => {
    try {
      const response = await api.patch(`/schedule/${scheduleId}`, { status: 'REJECTED' });
      
      if (response.data.success) {
        const schedule = schedules.PENDING.find(s => s.id === scheduleId);
        toast.error(`${schedule?.user.name} 님의 휴무 신청을 거절했습니다. ❌`);
        loadAllSchedules(); // 전체 재로드
      }
    } catch (error) {
      console.error('거절 실패:', error);
      toast.error('거절에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <main className="container mx-auto px-4 py-12 pb-0">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-primary mb-2">📋 휴무 승인 관리</h1>
          <p className="text-gray-600 mb-8">휴무 신청을 확인하고 처리하세요</p>
          <div className="text-center py-16">
            <div className="text-6xl mb-4 animate-flip">🍖</div>
            <p className="text-lg text-gray-600">로딩 중...</p>
          </div>
        </div>
      </main>
    );
  }

  const weekEndDate = endOfWeek(currentWeekStart, { weekStartsOn: 0 });
  const isCurrentWeek = 
    startOfWeek(new Date(), { weekStartsOn: 0 }).getTime() === currentWeekStart.getTime();
  
  // 해당 월의 몇 번째 주인지 계산 (1주차, 2주차, ...)
  const monthStart = startOfMonth(currentWeekStart);
  const weekOfMonth = differenceInWeeks(currentWeekStart, monthStart) + 1;

  return (
    <main className="container mx-auto px-4 py-12 pb-0">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-primary mb-2">📋 휴무 승인 관리</h1>
        <p className="text-gray-600 mb-6">휴무 신청을 확인하고 처리하세요</p>

        {/* 탭 메뉴 */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('PENDING')}
            className={`px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${
              activeTab === 'PENDING'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            대기 중 {schedules.PENDING.length}
          </button>
          <button
            onClick={() => setActiveTab('APPROVED')}
            className={`px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${
              activeTab === 'APPROVED'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            승인 {schedules.APPROVED.length}
          </button>
          <button
            onClick={() => setActiveTab('REJECTED')}
            className={`px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${
              activeTab === 'REJECTED'
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            거절 {schedules.REJECTED.length}
          </button>
        </div>

        {/* 주간 네비게이션 (승인/거절 탭만) */}
        {activeTab !== 'PENDING' && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={goToPrevWeek}
                className="px-2 py-1 text-sm bg-white rounded-lg font-bold text-purple-600 hover:bg-purple-100 transition-colors"
              >
                ◀ 이전 주
              </button>
              
              <div className="text-center flex-1">
                <div className="font-bold text-purple-800">
                  {format(currentWeekStart, 'yyyy년 M월', { locale: ko })} {weekOfMonth}주차
                </div>
                <div className="text-xs text-purple-600">
                  {format(currentWeekStart, 'M/d')} ~ {format(weekEndDate, 'M/d')}
                </div>
              </div>
              
              <button
                onClick={goToNextWeek}
                disabled={isCurrentWeek}
                className={`px-2 py-1 text-sm bg-white rounded-lg font-bold transition-colors ${
                  isCurrentWeek 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-purple-600 hover:bg-purple-100'
                }`}
              >
                다음 주 ▶
              </button>
            </div>
            
            {!isCurrentWeek && (
              <button
                onClick={goToThisWeek}
                className="w-full mt-2 py-2 bg-purple-500 text-white rounded-lg font-bold hover:bg-purple-600 transition-colors"
              >
                이번 주로 돌아가기
              </button>
            )}
            
            <div className="mt-2 text-xs text-center text-purple-600">
              이번 주: {currentSchedules.length}건
            </div>
          </div>
        )}

        {/* 사용법 안내 (대기 중 탭만) */}
        {activeTab === 'PENDING' && schedules.PENDING.length > 0 && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
            <h3 className="font-bold text-blue-800 mb-2">📖 사용법</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• 오른쪽으로 스와이프 → ✅ 승인</li>
              <li>• 왼쪽으로 스와이프 → ❌ 거절</li>
              <li>• 스와이프가 안 되면 하단 버튼 사용</li>
            </ul>
          </div>
        )}

        {/* 목록 */}
        {currentSchedules.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl">
            <div className="text-6xl mb-4">
              {activeTab === 'PENDING' && '✨'}
              {activeTab === 'APPROVED' && '📭'}
              {activeTab === 'REJECTED' && '📭'}
            </div>
            <p className="text-lg text-gray-600">
              {activeTab === 'PENDING' && '대기 중인 휴무 신청이 없습니다!'}
              {activeTab === 'APPROVED' && '이번 주 승인된 휴무 신청이 없습니다'}
              {activeTab === 'REJECTED' && '이번 주 거절된 휴무 신청이 없습니다'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {currentSchedules.map((schedule) => {
              const CardContent = (
                <div className="bg-white rounded-lg shadow-md p-4 border-2 border-gray-200">
                  {/* 첫 줄: 이름 | 휴무날짜 | 사유 */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="font-bold text-primary">{schedule.user.name}</span>
                      <span className="text-gray-400">|</span>
                      <span className="font-medium text-gray-700">
                        {format(new Date(schedule.date), 'M/d (EEE)', { locale: ko })}
                      </span>
                      <span className="text-gray-400">|</span>
                      <span className="text-sm text-gray-600 truncate">{schedule.reason}</span>
                    </div>
                    {activeTab !== 'PENDING' && (
                      <div className={`px-2 py-1 rounded text-xs font-bold ${
                        activeTab === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {activeTab === 'APPROVED' ? '✅' : '❌'}
                      </div>
                    )}
                  </div>
                  
                  {/* 둘째 줄: 요청날짜 (+ 처리날짜) */}
                  <div className="flex gap-3 text-xs text-gray-500">
                    <span>요청 {format(new Date(schedule.createdAt), 'M/d HH:mm', { locale: ko })}</span>
                    {activeTab !== 'PENDING' && (
                      <span>처리 {format(new Date(schedule.updatedAt), 'M/d HH:mm', { locale: ko })}</span>
                    )}
                  </div>
                </div>
              );

              // 대기 중만 스와이프 가능
              if (activeTab === 'PENDING') {
                return (
                  <SwipeableCard
                    key={schedule.id}
                    onApprove={() => handleApprove(schedule.id)}
                    onReject={() => handleReject(schedule.id)}
                  >
                    {CardContent}
                  </SwipeableCard>
                );
              }

              // 승인/거절은 읽기 전용
              return <div key={schedule.id}>{CardContent}</div>;
            })}
          </div>
        )}

        {/* 일반 버튼 (대기 중 탭 + 스와이프 못 할 때) */}
        {activeTab === 'PENDING' && schedules.PENDING.length > 0 && (
          <div className="mt-8 bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-3">스와이프가 안 되면 버튼을 사용하세요:</p>
            <div className="flex gap-2">
              <button
                onClick={() => handleApprove(schedules.PENDING[0].id)}
                className="flex-1 bg-green-500 text-white py-3 rounded-lg font-bold hover:bg-green-600 transition-colors"
              >
                ✅ 승인
              </button>
              <button
                onClick={() => handleReject(schedules.PENDING[0].id)}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-bold hover:bg-red-600 transition-colors"
              >
                ❌ 거절
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminSchedulePage;
